<template>
  <div class="left-nav">
    <NavLogo class="logo" :class="{ 'with-back': !!back }" />

    <template v-if="!loading">
      <NavSearch
        v-model="search"
        class="tw-mb-4 tw-mx-4"
        :class="{ 'tw-invisible': !isSearchable }"
      />
      <div class="left-nav-items">
        <div v-if="back" @click="back.action" class="left-nav-item left-nav-back">
          <IconButton
            contentPosition="right"
            class="nav-icon tw-flex tw-items-center tw-text-left"
            :icon="{
              default: { icon: 'fa-regular fa-chevron-down', rotation: 90, class: 'tw-w-4' },
              hover: { icon: 'fa-solid fa-chevron-down', rotation: 90, class: 'tw-w-4' },
            }"
            testId="nav-back-button"
          >
            <div class="nav-text tw-ml-2">
              {{ back.name ? truncate(back.name, 55) : "Go Back" }}
            </div>
          </IconButton>
        </div>
        <nav v-if="filteredMenuItems.length > 0">
          <div class="tw-mx-4">
            <NavLeftItem v-for="(item, index) in filteredMenuItems" :item="item" :key="index" />
          </div>

          <div v-if="hasAdvanced" class="tw-mx-4">
            <IconButton
              testId="nav-left-show-more-less-btn"
              @click="toggleShowAdvanced"
              contentPosition="right"
              class="show-more-button tw-flex tw-items-center tw-p-2 tw-w-full"
              :icon="{
                default: {
                  icon: 'fa-regular fa-chevron-down',
                  flip: showAdvanced ? 'vertical' : undefined,
                  class: 'tw-w-4',
                },
                hover: {
                  icon: 'fa-solid fa-chevron-down',
                  flip: showAdvanced ? 'vertical' : undefined,
                  class: 'tw-w-4',
                },
              }"
            >
              <div class="tw-text-sm tw-ml-2">Show {{ showAdvanced ? "Less" : "More" }}</div>
            </IconButton>
          </div>
        </nav>
        <NavSearchNoResults v-if="isSearchable && filteredMenuItems.length === 0" />
      </div>
    </template>

    <div v-else class="left-nav-items">
      <div class="loading-block-container">
        <div class="loading-block nav-loading-block-left"></div>
        <div class="loading-block nav-loading-block-right"></div>
      </div>
      <div class="loading-block-container">
        <div class="loading-block nav-loading-block-left"></div>
        <div class="loading-block nav-loading-block-right"></div>
      </div>
      <div class="loading-block-container">
        <div class="loading-block nav-loading-block-left"></div>
        <div class="loading-block nav-loading-block-right"></div>
      </div>
      <div class="loading-block-container">
        <div class="loading-block nav-loading-block-left"></div>
        <div class="loading-block nav-loading-block-right"></div>
      </div>
    </div>
    <div class="left-nav-portal-switch tw-px-2 tw-py-2">
      <PortalSwitch />
    </div>
    <nav class="left-nav-footer">
      <div class="left-nav-footer-help nx-text-body3">Let Us Help</div>
      <div class="left-nav-footer-icons">
        <NavLeftFooterIcon
          v-for="(helper, index) in helpItems"
          :key="index"
          class="left-nav-icon-container"
          @click="helper.action"
          :data-test="`nav-left-help-${formatDataTest(helper.icon?.default?.icon)}`"
          :icon="helper.icon"
        />
      </div>
      <a
        class="left-nav-kb"
        target="_blank"
        :href="KB_LINKS.HELP"
        rel="noopener noreferrer"
        data-test="nav-left-help-kb-link"
      >
        Browse the knowledge base
      </a>
    </nav>
  </div>
</template>

<script>
import { computed, ref, watch } from "vue";
import constants from "@/utils/constants";
import { formatters } from "leatherman-js";
import { fuzzy } from "fast-fuzzy";
import IconButton from "@/components/display/IconButton.vue";
import NavLeftFooterIcon from "@/components/nav/NavLeftFooterIcon.vue";
import NavLeftItem from "@/components/nav/NavLeftItem.vue";
import NavLink from "@/classes/NavLink";
import NavLogo from "@/components/nav/NavLogo";
import NavSearch from "@/components/nav/NavSearch";
import NavSearchNoResults from "@/components/nav/NavSearchNoResults";
import PortalSwitch from "@/components/smart/PortalSwitch";
import router from "@/router";

export default {
  name: "NavLeft",
  components: {
    IconButton,
    NavLeftFooterIcon,
    NavLeftItem,
    NavLogo,
    NavSearch,
    NavSearchNoResults,
    PortalSwitch,
  },
  props: {
    homeRoute: {
      type: String,
      default: constants.ROUTES.HOME,
    },
    back: NavLink,
    menuTitle: String,
    menuItems: {
      type: Array,
      default: () => [],
    },
    loading: Boolean,
    helpItems: {
      validator: (value) => {
        if (!Array.isArray(value)) {
          return false;
        }

        return value.filter((item) => !item.icon || !item.action).length === 0;
      },
    },
  },
  setup(props) {
    const search = ref(null);
    const showAdvanced = ref(false);
    const filteredMenuItems = computed(() => {
      if (!search.value) {
        return showAdvanced.value
          ? props.menuItems
          : props.menuItems.filter((item) => !item.advanced || routeMatches(item.routeName));
      }
      return props.menuItems.filter((menuItem) => {
        let match = false;
        const keywords = menuItem?.keywords || [];
        keywords.forEach((keyword) => {
          if (fuzzy(search.value, keyword, { ignoreSymbols: false }) >= 0.8) {
            match = true;
          }
        });
        return match;
      });
    });
    const hasAdvanced = computed(
      () => props.menuItems.filter((menuItem) => menuItem.advanced).length > 0
    );
    const isSearchable = computed(
      () => props.menuItems.filter((menuItem) => menuItem.keywords.length > 0).length > 0
    );

    const formatDataTest = (input) => {
      return input.toLowerCase().replace(/(,|_| |;)/g, "-");
    };

    const routeMatches = (routeName) => {
      const route = router?.currentRoute?.value;
      const matchedRoutes = [...route.matched];
      const match = matchedRoutes.reverse().find((route) => routeName === route?.name);
      return !!match;
    };

    const toggleShowAdvanced = () => (showAdvanced.value = !showAdvanced.value);

    watch(
      [router?.currentRoute],
      (current, previous) => {
        if (current[0].fullPath !== previous[0].fullPath) {
          search.value = null;
        }
      },
      { deep: true }
    );

    return {
      filteredMenuItems,
      formatDataTest,
      hasAdvanced,
      isSearchable,
      KB_LINKS: constants.LINKS.NEXCESS.KB,
      search,
      showAdvanced,
      routeMatches,
      toggleShowAdvanced,
      truncate: formatters.text.truncate,
    };
  },
};
</script>

<style lang="scss">
@import "~@/styles/variables";

.left-nav-icon-container:hover {
  cursor: pointer;
}

.left-nav {
  width: 215px;
  display: flex;
  flex-direction: column;
  height: 100%;

  .logo {
    margin-bottom: 1rem;

    &.with-back {
      margin-bottom: 1rem;
    }
  }

  .left-nav-items {
    flex-grow: 1;
    overflow-x: hidden;
    overflow-y: auto;
    // scroll-snap-type: y mandatory;

    /* Works on Chrome/Edge/Safari */
    &::-webkit-scrollbar {
      width: 12px;
    }
    &::-webkit-scrollbar-thumb {
      border-radius: 5px;
      border-width: 2px;
      border-style: solid;
    }

    /* Works on Firefox */
    scrollbar-width: thin;

    .left-nav-title {
      font-size: 0.875rem;
      display: flex;
      flex-direction: row;
      align-items: center;
      margin-bottom: 1rem;
      height: $nav-item-height;
      font-weight: 600;
      padding-left: 24px;
      padding-right: 10px;
      word-break: break-word;
    }

    .left-nav-item {
      font-size: 0.875rem;
      display: flex;
      flex-direction: row;
      align-items: center;
      cursor: pointer;
      padding: 0.5rem;
      margin-bottom: 0.5rem;
      scroll-snap-align: end;
      text-decoration: none;

      &:hover {
        text-decoration: none;
      }

      .nav-icon {
        margin-right: 1rem;
      }

      @-webkit-keyframes fadeInAppear {
        0% {
          opacity: 0;
        }
        100% {
          opacity: 1;
        }
      }

      @keyframes fadeInAppear {
        0% {
          opacity: 0;
        }
        100% {
          opacity: 1;
        }
      }

      &.advanced {
        -webkit-animation-name: fadeInAppear;
        animation-name: fadeInAppear;
        -webkit-animation-timing-function: ease-in;
        animation-timing-function: ease-in;
        -webkit-animation-duration: 0.5s;
        animation-duration: 0.5s;
        -webkit-animation-delay: 0s;
        animation-delay: 0s;
        -webkit-animation-fill-mode: forwards;
        animation-fill-mode: forwards;
        opacity: 0;
      }
    }

    .left-nav-back {
      font-size: 0.75rem;
      margin: 0 16px 0.5rem 16px;

      .nav-text {
        text-transform: uppercase;
      }
    }
  }

  .left-nav-footer {
    height: 125px;
    flex-grow: 0;
    padding: 20px 0;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;

    .left-nav-footer-help {
      margin: 0 24px 0 24px;
    }

    .left-nav-footer-icons {
      margin: 0 24px 0 24px;
      display: flex;
      align-content: center;
      gap: 31px;
    }

    a.left-nav-kb {
      margin: 0 24px 0 24px;
      font-size: 10px;
    }
  }
}
</style>
